import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Paper as MuiPaper,
  CardContent,
  Box,
  Chip,
  Button,
  Stack,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import { green, red, orange, blue, grey, amber } from "@mui/material/colors";
import PropTypes from "prop-types";
import AssignUser from "./assign";
import { useDialog } from "../../.././contexts/DialogContext";
import { getTimeStamp } from "../../.././utils/common";
import useWindowDimensions from "../../../utils/windowDimensions";
import useComplaint from "../../../hooks/useComplaint";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import Accept from "./Accept";
import UpdateComplaint from "./UpdateComplaint";
import CloseComplaint from "./CloseComplaint";

const STATUS_COLORS = {
  OPEN: red[500], //Open
  ASSIGNED: blue[500], //Assigned
  RE_ASSIGNED: orange[500], //Assigned
  IN_PROGRESS: amber[500], //In progress
  RESOLVED: green[500], //Resolved
  CLOSED: grey[600], //Closed
};
const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [
    {
      field: "customerComplaintId",
      sort: "ASC",
    },
  ],
};

const complaintStatus = ["ACCEPTED", "IN_PROGRESS"];

function statusChip(status) {
  const bgColor = STATUS_COLORS[status] || grey[400];
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: bgColor,
        color: "#fff", // white text for contrast
        fontWeight: "bold",
      }}
    />
  );
}

const CustomerComplaintDataGrid = (props) => {
  const [payload, setPayload] = useState({});
  const {
    complaintId,
    fetchData,
    selectedUser,
    setSelectedRows,
    isLoadedFromAddComplaintField,
    checkboxSelection,
    businessType,
  } = props;

  const { height, width } = useWindowDimensions();
  const { width: mainContentWidth } = useMainContentDimensions();
  const [sortModel, setSortModel] = useState([
    {
      field: "createdAt",
      sort: "desc",
    },
  ]);
  const {
    getComplaint,
    complaint: data,
    getAllCustomerComplaint,
    customerComplaints,
    paging,
    isLoading,
    setCustomerComplaints,
  } = useComplaint();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const { openDialog } = useDialog();
  const assignTo = (id, complaintId) => {
    return openDialog(
      "Assign",
      true,
      <AssignUser
        key={getTimeStamp()}
        customerComplaintId={id}
        complaintId={complaintId}
        fetchData={fetchData}
      />,
    );
  };

  const acceptComplaint = (id, complaintId) => {
    return openDialog(
      "Accept",
      true,
      <Accept
        key={getTimeStamp()}
        customerComplaintId={id}
        complaintId={complaintId}
        fetchData={fetchData}
      />,
    );
  };
  const updateComplaint = (id, complaintId) => {
    return openDialog(
      "update_complaint",
      "Update Complaint",
      <UpdateComplaint
        key={getTimeStamp()}
        customerComplaintId={id}
        complaintId={complaintId}
        fetchData={fetchData}
      />,
    );
  };
  const closeComplaint = (id, complaintId) => {
    return openDialog(
      "close_complaint",
      "Close Complaint",
      <CloseComplaint
        key={getTimeStamp()}
        customerComplaintId={id}
        complaintId={complaintId}
        fetchData={fetchData}
      />,
    );
  };

  const resolveComplaint = (id, complaintId) => {
    return openDialog(
      "close_complaint",
      "Resolve Complaint",
      <CloseComplaint
        key={getTimeStamp()}
        customerComplaintId={id}
        complaintId={complaintId}
        fetchData={fetchData}
      />,
    );
  };

  const fetchCustomerComplaintData = async () => {
    console.log(payload, "payload0909");
    if (Object.keys(payload).length != 0) {
      await getAllCustomerComplaint(payload);
    }
  };

  useEffect(() => {
    fetchCustomerComplaintData();
  }, [payload]);

  useEffect(() => {
    console.log(complaintId, "complaintId");
    if (!isLoadedFromAddComplaintField) {
      if (complaintId) {
        const payloadValues = {
          ...currentPayload,
          filters: [
            {
              fieldName: "complaintId",
              fieldValue: complaintId,
              op: "eq",
              dataType: "int",
              condition: "and",
              isJoin: true,
              attributeName: "complaint",
              entityName: "ComplaintEntity",
            },
          ],
        };
        console.log(payloadValues, "payloadValues");
        setPayload(payloadValues);
      } else {
        setPayload(currentPayload);
      }
    }
  }, [complaintId]);

  useEffect(() => {
    console.log(selectedUser, "hello");
    if (selectedUser) {
      const payloadValues = {
        ...currentPayload,
        filters: [
          {
            fieldName: "radUserId",
            fieldValue: selectedUser?.value,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "radUser",
            entityName: "RadUserEntity",
          },
        ],
      };
      setPayload(payloadValues);
    } else {
      setCustomerComplaints();
    }
  }, [selectedUser]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  let columns = [
    {
      field: "radUser",
      headerName: "User Name",
      valueGetter: (params) => {
        return params.userName;
      },
      width: 120,
    },
    {
      field: "complaintStatus",
      headerName: "Complaint Status",
      renderCell: (params) => statusChip(params.value),
      width: 200,
    },
    {
      field: "complaintDetails",
      headerName: "Complaint Details",
      type: "string",
      width: 500,
    },

    {
      field: "createdAt",
      headerName: "Reported At",
      type: "string",
      width: 200,
    },
  ];

  console.log(businessType, "businessType");

  if (!isLoadedFromAddComplaintField && businessType != "USER") {
    columns.push({
      field: "action",
      headerName: "Action",
      filter: false,
      sortable: false,
      width: 250,
      renderCell: (params) => {
        const onClick = (e) => {
          e.stopPropagation(); // don't select this row after clicking
          const api = params.api;
          const thisRow = {};
          api
            .getAllColumns()
            .filter((c) => c.field !== "__check__" && !!c)
            .forEach((c) => (thisRow[c.field] = params.getValue(params.id, c.field)));
          console.log(thisRow, "thisRow");
        };
        return !isLoadedFromAddComplaintField ? (
          <Stack direction="row" spacing={1}>
            {params.row.complaintStatus == "OPEN" && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={params.row.complaintStatus != "OPEN"}
                onClick={() => assignTo(params.row.customerComplaintId, params.row.complaintId)}
              >
                Assign
              </Button>
            )}
            {params.row.complaintStatus == "ASSIGNED" && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={params.row.complaintStatus != "ASSIGNED"}
                onClick={() =>
                  acceptComplaint(params.row.customerComplaintId, params.row.complaintId)
                }
              >
                Accept
              </Button>
            )}
            {complaintStatus.includes(params.row.complaintStatus) && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() =>
                  updateComplaint(params.row.customerComplaintId, params.row.complaintId)
                }
              >
                Update
              </Button>
            )}
            {params.row.complaintStatus == "IN_PROGRESS" && (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() =>
                  resolveComplaint(params.row.customerComplaintId, params.row.complaintId)
                }
              >
                Resolve
              </Button>
            )}
            {params.row.complaintStatus == "RESOLVED" && (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() =>
                  closeComplaint(params.row.customerComplaintId, params.row.complaintId)
                }
              >
                Close
              </Button>
            )}
          </Stack>
        ) : null;
      },
    });
  }

  return (
    <Box sx={{ flex: 1, overflow: "auto", width: mainContentWidth - 100 }}>
      <DataGrid
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        rowCount={totalNumberOfRecords}
        getRowId={(row) => row["customerComplaintId"]}
        columns={columns}
        rows={customerComplaints}
        page={paging.pageNumber}
        pageSize={paging.pageSize}
        paginationMeta={paginationMeta}
        loading={isLoading}
        pageSizeOptions={[25, 50, 100]}
        paginationMode="server"
        onPaginationModelChange={onPageChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        paginationModel={paginationModel}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setSelectedRows(newRowSelectionModel);
        }}
        sx={{
          "& .MuiDataGrid-row.even-row": {
            backgroundColor: "#f9f9f9", // Light gray for even rows
          },
          "& .MuiDataGrid-row.odd-row": {
            backgroundColor: "#ffffff", // White for odd rows
          },
        }}
      />
    </Box>
  );
};

export default CustomerComplaintDataGrid;
